import { boolean, mixed, number, object, string } from 'yup';
import type { DocumentMode } from 'types/documentEditing';
import { GUID_REGEX } from 'utils/regex';
import { isRecord } from 'utils/typeChecks';

const invalidSessionMessage = 'Invalid document session';
const sessionString = string()
  .defined()
  .test('primitive-string', invalidSessionMessage, (value) => typeof value === 'string');
const safeInteger = number().defined().test('safe-integer', invalidSessionMessage, Number.isSafeInteger);

const isDocumentSessionUrl = (value: string, origin?: string): boolean => {
  try {
    const url = new URL(value);
    return (
      url.protocol === 'https:' &&
      (origin === undefined || url.origin === origin) &&
      !url.username &&
      !url.password &&
      !url.searchParams.has('access_token')
    );
  } catch {
    return false;
  }
};

export const createDocumentSessionSchema = (fileId: string, origin: string, now: number) =>
  object({
    fileId: sessionString.oneOf([fileId]),
    sessionId: sessionString.matches(GUID_REGEX),
    mode: mixed<DocumentMode>().defined().oneOf(['view', 'edit', 'review']),
    canEdit: boolean().defined(),
    canReview: boolean().defined(),
    actionUrl: sessionString.test('trusted-origin', invalidSessionMessage, (value) => isDocumentSessionUrl(value, origin)),
    wopiSrc: sessionString.test('secure-source', invalidSessionMessage, (value) => isDocumentSessionUrl(value)),
    accessToken: sessionString.required(),
    accessTokenTtl: safeInteger.moreThan(now),
    accessTokenTtlSeconds: safeInteger.positive(),
    expiresAtUtc: sessionString,
  })
    .required()
    .test('session-object', invalidSessionMessage, isRecord)
    .test('consistent-expiry', invalidSessionMessage, ({ expiresAtUtc, accessTokenTtl }) => {
      const expiresAt = Date.parse(expiresAtUtc);
      return Number.isFinite(expiresAt) && Math.abs(expiresAt - accessTokenTtl) <= 1000;
    })
    .test(
      'mode-permissions',
      invalidSessionMessage,
      ({ mode, canEdit, canReview }) => canEdit === (mode === 'edit') && canReview === (mode === 'review')
    );
